import { ListItemButton, ListItemIcon, ListItemText, Skeleton, Typography } from '@mui/material'
import { Stack } from '@mui/system'

import { usePaginatedFields } from '@cibo/landmanager'
import { useAllProgramContent } from '@cibo/programs'
import { useFigures } from '@cibo/ui'
import { AgreementModel, AgreementStatusChip } from 'features/Agreements'
import { flatten, sum, uniqBy } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const UserOfferListItem = ({
  agreement,
  onClickAgreement,
}: {
  agreement: AgreementModel
  onClickAgreement: (agreementId: string) => () => void
}) => {
  const { t } = useTranslation('impact/pages/UserAgreements')
  const { modifiedDate } = useFigures()
  const { earnedAmount } = useFigures()
  const fieldSearch = usePaginatedFields(0, 1000, {
    agreementId: agreement.agreementId,
    includeAllResolved: true,
  })
  const programContent = useAllProgramContent()
  const programs = useMemo(() => {
    const programArray = uniqBy(
      a => a && a.programId,
      flatten(fieldSearch.data?.items.map(i => i.participatingPrograms) || [])
    )
    return programArray
      .map(
        prog =>
          programContent.data?.find(({ programId }) => prog && prog.programId === programId)
            ?.content.fields.title
      )
      .join(', ')
  }, [fieldSearch.isFetched, programContent.isFetched])
  const offer = useMemo(() => sum(fieldSearch.data?.items.map(i => i.offerPayment()) || []), [
    fieldSearch.isFetched,
  ])

  return (
    <ListItemButton sx={{ height: '72px' }} onClick={onClickAgreement(agreement.agreementId)}>
      <ListItemIcon sx={{ alignSelf: 'start', width: '90px' }}>
        <AgreementStatusChip agreement={agreement} concise />
      </ListItemIcon>
      <ListItemText sx={{ marginInline: 2 }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack spacing={1}>
            <Typography color="secondary">
              {t('agreementName', { context: agreement.agreementType })}
            </Typography>
            <Typography variant="caption" color="secondary">
              {fieldSearch.isFetching || programContent.isFetching ? (
                <Skeleton width="120px" />
              ) : (
                t('offerDetails', {
                  amount: offer
                    ? earnedAmount({
                        amount: offer,
                        token: 'usd', // TODO: change when available on API
                      })
                    : '$--',
                  count: fieldSearch.data?.numAvailable,
                })
              )}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignSelf="center" alignItems="center">
            <Typography color="secondary">
              {fieldSearch.isFetching || programContent.isFetching ? (
                <Skeleton width="200px" />
              ) : (
                programs
              )}
            </Typography>
            <Typography variant="caption" color="secondary">
              {modifiedDate(agreement.modifiedDate)}
            </Typography>
          </Stack>
        </Stack>
      </ListItemText>
    </ListItemButton>
  )
}
